/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import { Typography2 } from '@coursera/cds-core';

import SearchExplainContext from 'bundles/search-common/components/internal-search-explainer/SearchExplainContext';
import type {
  SearchExplanationMetadata,
  SearchProductHitWithExplanation,
} from 'bundles/search-common/components/internal-search-explainer/types';

import _t from 'i18n!nls/search-common';

type SearchExplainHoverProps = {
  children: React.ReactNode;
  hit: SearchProductHitWithExplanation;
  query: string;
};

const HOVER_CARD_WIDTH = 420; // px - Width for hover card
const HOVER_CARD_Z_INDEX = 99999; // Above all content
const CURSOR_OFFSET = 0; // px - Distance from cursor
const CARD_APPROX_HEIGHT = 250; // px - Approximate height for positioning
const JSON_DISPLAY_FONT_SIZE = 11; // px - Small monospace font for JSON

const styles = {
  container: css`
    /* Container should be completely transparent to layout */
    display: contents;
  `,
  hoverCard: css`
    position: fixed;
    width: ${HOVER_CARD_WIDTH}px;
    z-index: ${HOVER_CARD_Z_INDEX};
    background-color: var(--cds-color-white-0) !important;
    border: 2px solid var(--cds-color-grey-400);
    border-radius: 8px;
    padding: var(--cds-spacing-200);

    /* Ensure completely opaque and above everything */
    opacity: 1 !important;
    isolation: isolate;

    /* Smooth transition for position changes */
    transition: all 0.1s ease-out;
  `,
  section: css`
    margin-bottom: var(--cds-spacing-100);
    padding: var(--cds-spacing-100);
    border: 1px solid var(--cds-color-grey-200);
    border-radius: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  `,
  sectionTitle: css`
    font-weight: 600;
    color: var(--cds-color-grey-800);
    margin-bottom: var(--cds-spacing-200);
  `,
  title: css`
    font-weight: 700;
    font-size: 1.1em;
    color: var(--cds-color-grey-800);
    margin-bottom: var(--cds-spacing-100);
  `,
  subtitle: css`
    color: var(--cds-color-grey-600);
    margin-bottom: var(--cds-spacing-300);
    font-style: italic;
    padding-left: var(--cds-spacing-200);
  `,
  querySubheading: css`
    color: var(--cds-color-grey-600);
    margin-bottom: var(--cds-spacing-300);
    font-style: italic;
    font-size: 0.875rem;
  `,
  explanationText: css`
    color: var(--cds-color-grey-700);
    line-height: 1.4;
  `,
  metricRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--cds-spacing-100) 0;
    border-bottom: 1px solid var(--cds-color-grey-100);

    &:last-child {
      border-bottom: none;
    }
  `,
  metricRowDouble: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--cds-spacing-100) 0;
    border-bottom: 1px solid var(--cds-color-grey-100);
    gap: var(--cds-spacing-200);

    &:last-child {
      border-bottom: none;
    }
  `,
  metricPair: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1;
    min-width: 0;
  `,
  metricLabel: css`
    color: var(--cds-color-grey-600);
    font-size: 0.875rem;

    /* Add cursor pointer for labels with tooltips */
    &[title]:not([title='']) {
      cursor: help;
      text-decoration: underline;
      text-decoration-style: dotted;
    }
  `,
  metricValue: css`
    color: var(--cds-color-grey-800);
    font-weight: 500;
    font-size: 0.875rem;
  `,
  businessRuleNotice: css`
    color: var(--cds-color-yellow-700);
    font-style: italic;
    font-size: 0.875rem;
  `,
  featuresSection: css`
    margin-top: var(--cds-spacing-200);
  `,
  featuresToggle: css`
    cursor: pointer;
    user-select: none;
    padding: var(--cds-spacing-100);
    border-radius: 4px;

    &:hover {
      background-color: var(--cds-color-grey-100);
    }

    &:focus {
      outline: 2px solid var(--cds-color-blue-600);
      outline-offset: 2px;
    }
  `,
  featuresContent: css`
    margin-top: var(--cds-spacing-100);
    padding: var(--cds-spacing-200);
    background-color: var(--cds-color-grey-50);
    border-radius: 4px;
    border: 1px solid var(--cds-color-grey-200);
  `,
  jsonDisplay: css`
    font-family: 'Courier New', monospace;
    font-size: ${JSON_DISPLAY_FONT_SIZE}px;
    color: var(--cds-color-grey-700);
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 200px;
    overflow-y: auto;
  `,
};

// Helper function to remove typed prefixes from product IDs
const normalizeProductId = (id: string): string => {
  if (!id) return '';

  // Remove prefixes like "course~", "specialization~", "s12n~", etc.
  const prefixRegex = /^[a-zA-Z0-9]+~/;
  const normalized = id.replace(prefixRegex, '');

  return normalized;
};

const formatMetricValue = (key: string, value: number): string => {
  if (value == null || typeof value !== 'number') {
    return 'N/A';
  }

  if (key.includes('Rate') || key === 'ctr') {
    return `${value}%`;
  }
  return value.toLocaleString();
};

const getMetricDisplayName = (key: string): string => {
  const displayNames: Record<string, string> = {
    ctr: _t('CTR'),
    viewers: _t('Viewers'),
    enrollers: _t('Enrollers'),
    enrollmentRate: _t('ER'),
    clickers: _t('Clickers'),
    paidConversionRate: _t('PCR'),
    paidEnrollers: _t('Paid Enrollers'),
  };

  return displayNames[key] || key;
};

const getMetricTooltip = (key: string): string => {
  const tooltips: Record<string, string> = {
    ctr: _t('Click-through Rate'),
    enrollmentRate: _t('Enrollment Rate'),
    paidConversionRate: _t('Paid Conversion Rate'),
  };

  return tooltips[key] || '';
};

/**
 * Renders the ranking explanation in the specified format
 */
const renderRankingExplanation = (
  explanationMetadata: SearchExplanationMetadata | null,
  showFeatures: boolean,
  setShowFeatures: (show: boolean) => void
) => {
  if (!explanationMetadata) return null;

  const {
    semanticScore,
    semanticMatchRank,
    lexicalScore,
    lexicalMatchRank,
    heavyRankerRank,
    postBusinessRulePlacementRank,
    heavyRankerFeatures,
  } = explanationMetadata;

  const businessRuleChanged = heavyRankerRank !== postBusinessRulePlacementRank;

  return (
    <>
      {/* Semantic Score, Rank */}
      {(semanticScore != null || semanticMatchRank != null) && (
        <div css={styles.metricRowDouble}>
          {semanticScore != null && (
            <div css={styles.metricPair}>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                {_t('Semantic Score')}
              </Typography2>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                {semanticScore.toFixed(3)}
              </Typography2>
            </div>
          )}
          {semanticMatchRank != null && (
            <div css={styles.metricPair}>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                {_t('Rank')}
              </Typography2>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                {semanticMatchRank}
              </Typography2>
            </div>
          )}
        </div>
      )}

      {/* Light Ranker Score, Rank */}
      {(lexicalScore != null || lexicalMatchRank != null) && (
        <div css={styles.metricRowDouble}>
          {lexicalScore != null && (
            <div css={styles.metricPair}>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                {_t('Light Ranker Score')}
              </Typography2>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                {lexicalScore.toFixed(3)}
              </Typography2>
            </div>
          )}
          {lexicalMatchRank != null && (
            <div css={styles.metricPair}>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                {_t('Rank')}
              </Typography2>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                {lexicalMatchRank}
              </Typography2>
            </div>
          )}
        </div>
      )}

      {/* Heavy Ranker, Final Rank */}
      {(heavyRankerRank != null || postBusinessRulePlacementRank != null) && (
        <div css={styles.metricRowDouble}>
          {heavyRankerRank != null && (
            <div css={styles.metricPair}>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                {_t('Heavy Ranker')}
              </Typography2>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                {heavyRankerRank}
              </Typography2>
            </div>
          )}
          {postBusinessRulePlacementRank != null && (
            <div css={styles.metricPair}>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                {_t('Final Rank')}
              </Typography2>
              <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                {postBusinessRulePlacementRank}
              </Typography2>
            </div>
          )}
        </div>
      )}

      {/* Business Rules Notice */}
      {businessRuleChanged && (
        <div css={styles.metricRow}>
          <Typography2 component="span" variant="bodySecondary" css={styles.businessRuleNotice}>
            {`Ranking changed by business rules from ${heavyRankerRank} to ${postBusinessRulePlacementRank}`}
          </Typography2>
        </div>
      )}

      {/* Heavy Ranker Features */}
      {heavyRankerFeatures && (
        <div css={styles.featuresSection}>
          <div
            css={styles.featuresToggle}
            onClick={() => setShowFeatures(!showFeatures)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowFeatures(!showFeatures);
              }
            }}
          >
            <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
              {_t('Heavy Ranker Features')} {showFeatures ? '▼' : '▶'}
            </Typography2>
          </div>
          {showFeatures && (
            <div css={styles.featuresContent}>
              <pre css={styles.jsonDisplay}>{JSON.stringify(heavyRankerFeatures, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Component that wraps search cards to show explanation metadata and content metrics on hover
 */
const SearchExplainHover = ({ children, hit, query }: SearchExplainHoverProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showFeatures, setShowFeatures] = useState(false);

  // Safely access context - return children if context not available
  const context = useContext(SearchExplainContext);

  if (!context || !context.enableSearchExplain || !query || !query.trim()) {
    return <>{children}</>;
  }

  const { queryMetrics } = context;

  // Find content metrics for this product
  const normalizedHitId = normalizeProductId(hit.id ?? '');
  const contentMetrics = queryMetrics?.content_metrics?.find((metric) => metric.clickedProduct === normalizedHitId);

  // Only show hover if we have explanation data or content metrics
  const hasData = hit.explanationMetadata || contentMetrics;

  if (!hasData) {
    return <>{children}</>;
  }

  // Calculate position for hover card (offset from mouse position)
  const getHoverCardStyle = () => {
    let left = mousePosition.x + CURSOR_OFFSET;
    let top = mousePosition.y + CURSOR_OFFSET;

    // Keep card within viewport bounds
    if (left + HOVER_CARD_WIDTH > window.innerWidth) {
      left = mousePosition.x - HOVER_CARD_WIDTH - CURSOR_OFFSET;
    }
    if (top + CARD_APPROX_HEIGHT > window.innerHeight) {
      top = mousePosition.y - CARD_APPROX_HEIGHT - CURSOR_OFFSET;
    }

    return { left: `${left}px`, top: `${top}px` };
  };

  const hoverCard = isHovered ? (
    <div
      css={styles.hoverCard}
      style={getHoverCardStyle()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Typography2 component="p" variant="bodyPrimary" css={styles.title}>
        <strong>{hit.name || hit.translatedName || 'Unknown Product'}</strong>
        {hit.partners && hit.partners.length > 0 && <span> by {hit.partners[0]}</span>}
      </Typography2>
      {query && query.trim() && (
        <Typography2 component="span" variant="bodySecondary" css={styles.querySubheading}>
          {_t('Query: "#{query}"', { query })}
        </Typography2>
      )}

      {/* Ranking Explanation */}
      {hit.explanationMetadata && (
        <div css={styles.section}>
          <Typography2 component="span" variant="bodySecondary" css={styles.sectionTitle}>
            {_t('Ranking Explanation')}
          </Typography2>
          <div css={styles.explanationText}>
            {renderRankingExplanation(hit.explanationMetadata, showFeatures, setShowFeatures)}
          </div>
        </div>
      )}

      {/* Content Metrics */}
      {contentMetrics && (
        <div css={styles.section}>
          <Typography2 component="span" variant="bodySecondary" css={styles.sectionTitle}>
            {_t('Content Metrics')}
          </Typography2>
          <Typography2 component="span" variant="bodySecondary" css={styles.subtitle}>
            {_t('Data from the last 28 days')}
          </Typography2>

          {/* Viewers - first column only */}
          {contentMetrics.viewers != null && (
            <div css={styles.metricRowDouble}>
              <div css={styles.metricPair}>
                <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                  {getMetricDisplayName('viewers')}
                </Typography2>
                <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                  {formatMetricValue('viewers', contentMetrics.viewers)}
                </Typography2>
              </div>
              <div css={styles.metricPair}>{/* Empty second column */}</div>
            </div>
          )}

          {/* Clickers and CTR - same row */}
          {(contentMetrics.clickers != null || contentMetrics.ctr != null) && (
            <div css={styles.metricRowDouble}>
              {contentMetrics.clickers != null && (
                <div css={styles.metricPair}>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                    {getMetricDisplayName('clickers')}
                  </Typography2>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                    {formatMetricValue('clickers', contentMetrics.clickers)}
                  </Typography2>
                </div>
              )}
              {contentMetrics.ctr != null && (
                <div css={styles.metricPair}>
                  <Typography2
                    component="span"
                    variant="bodySecondary"
                    css={styles.metricLabel}
                    title={getMetricTooltip('ctr')}
                  >
                    {getMetricDisplayName('ctr')}
                  </Typography2>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                    {formatMetricValue('ctr', contentMetrics.ctr)}
                  </Typography2>
                </div>
              )}
            </div>
          )}

          {/* Enrollers and Enrollment Rate - same row */}
          {(contentMetrics.enrollers != null || contentMetrics.enrollmentRate != null) && (
            <div css={styles.metricRowDouble}>
              {contentMetrics.enrollers != null && (
                <div css={styles.metricPair}>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                    {getMetricDisplayName('enrollers')}
                  </Typography2>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                    {formatMetricValue('enrollers', contentMetrics.enrollers)}
                  </Typography2>
                </div>
              )}
              {contentMetrics.enrollmentRate != null && (
                <div css={styles.metricPair}>
                  <Typography2
                    component="span"
                    variant="bodySecondary"
                    css={styles.metricLabel}
                    title={getMetricTooltip('enrollmentRate')}
                  >
                    {getMetricDisplayName('enrollmentRate')}
                  </Typography2>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                    {formatMetricValue('enrollmentRate', contentMetrics.enrollmentRate)}
                  </Typography2>
                </div>
              )}
            </div>
          )}

          {/* Paid Enrollers and Paid Conversion Rate - same row */}
          {(contentMetrics.paidEnrollers != null || contentMetrics.paidConversionRate != null) && (
            <div css={styles.metricRowDouble}>
              {contentMetrics.paidEnrollers != null && (
                <div css={styles.metricPair}>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricLabel}>
                    {getMetricDisplayName('paidEnrollers')}
                  </Typography2>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                    {formatMetricValue('paidEnrollers', contentMetrics.paidEnrollers)}
                  </Typography2>
                </div>
              )}
              {contentMetrics.paidConversionRate != null && (
                <div css={styles.metricPair}>
                  <Typography2
                    component="span"
                    variant="bodySecondary"
                    css={styles.metricLabel}
                    title={getMetricTooltip('paidConversionRate')}
                  >
                    {getMetricDisplayName('paidConversionRate')}
                  </Typography2>
                  <Typography2 component="span" variant="bodySecondary" css={styles.metricValue}>
                    {formatMetricValue('paidConversionRate', contentMetrics.paidConversionRate)}
                  </Typography2>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <div
        css={styles.container}
        onMouseEnter={(e) => {
          setMousePosition({ x: e.clientX, y: e.clientY });
          setIsHovered(true);
        }}
        onMouseMove={(e) => {
          setMousePosition({ x: e.clientX, y: e.clientY });
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </div>
      {isHovered && hoverCard && createPortal(hoverCard, document.body)}
    </>
  );
};

export default SearchExplainHover;
